import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import './styles.css';

/**
 * 儀表板組件
 * 顯示系統概覽數據和統計信息
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Object} props.user - 用戶信息
 * @returns {JSX.Element} 儀表板組件
 */
function Dashboard({ token, user }) {
    // 狀態
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [statusStats, setStatusStats] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [apiUsageTrend, setApiUsageTrend] = useState([]);

    // 獲取儀表板數據
    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);

            try {
                // 獲取系統概覽數據
                const statsResponse = await axios.get('/api/dashboard/stats', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (statsResponse.data.success) {
                    setStats(statsResponse.data.stats);
                }

                // 獲取保養單狀態分佈
                const statusResponse = await axios.get('/api/dashboard/warranty-status', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (statusResponse.data.success) {
                    setStatusStats(statusResponse.data.statusStats);
                }

                // 獲取最近活動
                const activityResponse = await axios.get('/api/dashboard/recent-activity', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (activityResponse.data.success) {
                    setRecentActivity(activityResponse.data.recentActivity);
                }

                // 如果是管理員，獲取API使用趨勢
                if (user?.role === 'admin') {
                    const apiTrendResponse = await axios.get('/api/dashboard/api-usage-trend', {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (apiTrendResponse.data.success) {
                        setApiUsageTrend(apiTrendResponse.data.apiUsageTrend);
                    }
                }
            } catch (err) {
                console.error('獲取儀表板數據時出錯:', err);
                setError('獲取儀表板數據時出錯: ' + (err.response?.data?.error || err.message));
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [token, user]);

    // 格式化日期
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // 獲取狀態顏色
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return '#f39c12'; // 黃色
            case 'approved':
                return '#2ecc71'; // 綠色
            case 'rejected':
                return '#e74c3c'; // 紅色
            default:
                return '#95a5a6'; // 灰色
        }
    };

    // 渲染統計卡片
    const renderStatCard = (title, value, icon, color) => (
        <div className="stat-card" style={{ borderTop: `3px solid ${color}` }}>
            <div className="stat-icon" style={{ color }}>
                {icon}
            </div>
            <div className="stat-content">
                <div className="stat-value">{value}</div>
                <div className="stat-title">{title}</div>
            </div>
        </div>
    );

    // 渲染狀態分佈
    const renderStatusDistribution = () => {
        if (!statusStats) return null;

        const total = statusStats.pending + statusStats.approved + statusStats.rejected;
        if (total === 0) return <p>暫無保養單數據</p>;

        return (
            <div className="status-distribution">
                <div className="status-bar">
                    <div
                        className="status-segment"
                        style={{
                            width: `${(statusStats.pending / total) * 100}%`,
                            backgroundColor: getStatusColor('pending')
                        }}
                        title={`待審核: ${statusStats.pending}`}
                    />
                    <div
                        className="status-segment"
                        style={{
                            width: `${(statusStats.approved / total) * 100}%`,
                            backgroundColor: getStatusColor('approved')
                        }}
                        title={`已通過: ${statusStats.approved}`}
                    />
                    <div
                        className="status-segment"
                        style={{
                            width: `${(statusStats.rejected / total) * 100}%`,
                            backgroundColor: getStatusColor('rejected')
                        }}
                        title={`已拒絕: ${statusStats.rejected}`}
                    />
                </div>
                <div className="status-legend">
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: getStatusColor('pending') }} />
                        <span className="legend-label">待審核: {statusStats.pending}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: getStatusColor('approved') }} />
                        <span className="legend-label">已通過: {statusStats.approved}</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: getStatusColor('rejected') }} />
                        <span className="legend-label">已拒絕: {statusStats.rejected}</span>
                    </div>
                </div>
            </div>
        );
    };

    // 渲染最近活動
    const renderRecentActivity = () => {
        if (!recentActivity || recentActivity.length === 0) {
            return <p className="empty-message">暫無活動記錄</p>;
        }

        return (
            <div className="activity-list">
                {recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                        <div className="activity-time">{formatDate(activity.timestamp)}</div>
                        <div className="activity-content">
                            <div className="activity-user">{activity.username}</div>
                            <div className="activity-action">{activity.action}</div>
                            <div className="activity-details">{activity.details}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // 渲染API使用趨勢
    const renderApiUsageTrend = () => {
        if (!apiUsageTrend || apiUsageTrend.length === 0) {
            return <p className="empty-message">暫無API使用數據</p>;
        }

        // 計算最大值以確定圖表高度
        const maxCount = Math.max(...apiUsageTrend.map(day => day.count));

        return (
            <div className="api-trend-chart">
                {apiUsageTrend.map((day, index) => (
                    <div key={index} className="trend-bar-container" title={`${day.date}: ${day.count} 請求`}>
                        <div
                            className="trend-bar"
                            style={{
                                height: `${(day.count / maxCount) * 100}%`,
                                backgroundColor: '#3498db'
                            }}
                        >
                            <div
                                className="trend-bar-success"
                                style={{
                                    height: `${(day.successful / day.count) * 100}%`,
                                    backgroundColor: '#2ecc71'
                                }}
                            />
                        </div>
                        <div className="trend-date">{day.date.split('-').slice(1).join('/')}</div>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return <Loading text="正在加載儀表板數據..." />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <div className="dashboard-container">
            <h1 className="dashboard-title">系統概覽</h1>

            {/* 統計卡片 */}
            <div className="stats-grid">
                {renderStatCard('客戶總數', stats?.customerCount || 0, '👥', '#3498db')}
                {renderStatCard('保養單總數', stats?.warrantyCount || 0, '📋', '#9b59b6')}
                {renderStatCard('今日新增保養單', stats?.todayNewWarranties || 0, '📝', '#2ecc71')}
                {user?.role === 'admin' && renderStatCard('API密鑰數量', stats?.apiKeyCount || 0, '🔑', '#e67e22')}
                {user?.role === 'admin' && renderStatCard('用戶數量', stats?.userCount || 0, '👤', '#1abc9c')}
                {user?.role === 'admin' && renderStatCard('今日API請求', stats?.todayApiRequests || 0, '🔄', '#f39c12')}
            </div>

            <div className="dashboard-row">
                {/* 保養單狀態分佈 */}
                <Card title="保養單狀態分佈" className="dashboard-card status-card">
                    {renderStatusDistribution()}
                </Card>

                {/* 快速操作 */}
                <Card title="快速操作" className="dashboard-card actions-card">
                    <div className="quick-actions">
                        <Link to="/customers">
                            <Button variant="primary" className="primary">查看客戶列表</Button>
                        </Link>
                        <Link to="/warranties">
                            <Button variant="secondary" className="secondary">查看保養單列表</Button>
                        </Link>
                        {user?.role === 'admin' && (
                            <>
                                <Link to="/logs">
                                    <Button variant="info" className="info">查看系統日誌</Button>
                                </Link>
                                <Link to="/api-keys">
                                    <Button variant="warning" className="warning">管理API密鑰</Button>
                                </Link>
                                <Link to="/users">
                                    <Button variant="danger" className="danger">管理用戶</Button>
                                </Link>
                            </>
                        )}
                    </div>
                </Card>
            </div>

            <div className="dashboard-row">
                {/* 最近活動 */}
                <Card title="最近活動" className="dashboard-card activity-card">
                    {renderRecentActivity()}
                </Card>

                {/* API使用趨勢 (僅管理員可見) */}
                {user?.role === 'admin' && (
                    <Card title="API使用趨勢 (最近7天)" className="dashboard-card trend-card">
                        {renderApiUsageTrend()}
                    </Card>
                )}
            </div>
        </div>
    );
}

export default Dashboard; 